import { useState } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";
import instance from "./client"
import { refreshTokens } from "../services/Authentication";
import { useBoundStore } from "../store";

/** 402 codes meaning the organization has no access (see orgAccess) */
const ACCESS_CODES = new Set([
  "TRIAL_EXPIRED",
  "TRIAL_TOKEN_LIMIT",
  "ORG_PAUSED",
  "ORG_CANCELLED",
  "PLAN_ENDED",
]);

// In-flight refresh shared across concurrent 401s, so a burst of requests
// (e.g. on page load with an expired access token) triggers exactly one
// /auth/token call instead of racing — concurrent refreshes would otherwise
// rotate the refresh token out from under each other and fail.
let refreshPromise: Promise<string | null> | null = null;

const SetupInterceptors = (navigate: NavigateFunction) => {
  // Clear all credentials and send the user to login. Used whenever the
  // session can no longer be recovered (refresh token missing/expired/invalid)
  // so a stale stored credential self-heals instead of stranding the user on a
  // half-broken page with a raw "Unauthorized" toast.
  const forceLogout = () => {
    const { setAccessToken, setRefreshToken, setUser } = useBoundStore.getState();
    setAccessToken("");
    setRefreshToken("");
    setUser(null);
    refreshPromise = null;
    navigate("/login");
  };

  // Run a single refresh, returning the new access token or null on failure.
  const runRefresh = async (): Promise<string | null> => {
    const { refreshToken, setAccessToken, setRefreshToken } =
      useBoundStore.getState();
    if (!refreshToken) {
      return null;
    }
    const response = await refreshTokens(refreshToken);
    if (response.success && response.data) {
      setAccessToken(response.data.accessToken);
      setRefreshToken(response.data.refreshToken);
      return response.data.accessToken;
    }
    return null;
  };

  // Add a request interceptor
  instance.interceptors.request.use(
    (config) => {
      if (config.headers["Authorization"]) {
        return config;
      }

      const { accessToken } = useBoundStore.getState();
      if (accessToken) {
        config.headers["Authorization"] = "Bearer " + accessToken;
      }
      return config;
    },
    (error) => {
      Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;

      // The refresh request itself failed → the session is unrecoverable.
      if (status === 401 && originalRequest?.url === "/auth/token") {
        forceLogout();
        return Promise.reject(error);
      }

      // Handle 402 Payment Required (subscription expired).
      // Do not redirect exempt users - they should never hit this, but guard
      // against race conditions where the backend returns 402 before the
      // client-side exempt flag is populated.
      if (status === 402) {
        // Only losing access leads to the "no access" page; a trial limit
        // (e.g. TRIAL_PLAYBOOK_LIMIT) is shown by the screen that hit it
        const code = error.response?.data?.errors?.[0]?.title;
        const { user } = useBoundStore.getState();
        if (ACCESS_CODES.has(code) && !user?.isExemptFromSubscription) {
          navigate("/subscription-expired");
        }
        return Promise.reject(error);
      }

      if (status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;

        // Coalesce concurrent refreshes into one in-flight call.
        if (!refreshPromise) {
          refreshPromise = runRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        const newAccessToken = await refreshPromise;

        if (newAccessToken) {
          originalRequest.headers.Authorization = "Bearer " + newAccessToken;
          return instance(originalRequest);
        }

        // Refresh failed (no/expired/invalid refresh token): log out cleanly
        // rather than leaving the caller to surface an "Unauthorized" toast.
        forceLogout();
      }
      return Promise.reject(error);
    }
  );
}

export function NavigateFunctionComponent() {
  let navigate = useNavigate();
  const [ran, setRan] = useState(false);

  // only run setup once
  if (!ran) {
    SetupInterceptors(navigate);
    setRan(true);
  }
  return <></>;
}
